import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import Loading from '../common/Loading';
import default_image from '../default-image.png';
import Api from '../common/Api.js'
import jwt_encode from 'jwt-encode'
import jwt_decode from "jwt-decode";
import Footer from '../common/Footer'
import Header from '../common/Header'
import InfiniteScroll from "react-infinite-scroll-component";
import Select from 'react-select';

// Shared toast options
const toastOpts = {
  position: "bottom-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

// Keeps the Select control a fixed height (single-line + ellipsis)
const selectStyles = {
  control: (base) => ({ ...base, minHeight: 38, height: 38 }),
  valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', overflow: 'hidden' }),
  singleValue: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
  }),
  placeholder: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
};

function Shops() {
  const home = useLocation();
  const navigate = useNavigate();

  const [pageloading, setpageLoading] = useState(false);
  const [shops, setShops] = useState(0);
  const [count, setcount] = useState(0);
  const [counter, setCounter] = useState(0);
  const [categories, setCategories] = useState(null);
  const [search, setSearch] = useState('');
  const [searchCity, setCity] = useState('');

  // State — used only to scope the City dropdown
  const [states, setStates] = useState([]);
  const [searchState, setSearchState] = useState(null); // { value: 'MH', label: 'Maharashtra' }

  // Cities fetched on demand
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);

  const [searchCat, findCategory] = useState(0);
  const [cat_name, setCatName] = useState("All");
  const [customer, setCustomer] = useState(0);

  // ---------------------------------------------------------------------------
  // Initial load
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchdata = async () => {
      setpageLoading(true);

      // Defaults
      let data = { category: 0, search: '', city: '', state_code: '', customer: 0 };

      const stored = JSON.parse(localStorage.getItem('login-token') || 'null');
      if (stored) {
        data.customer = stored.id;
        setCustomer(stored.id);
      }

      // Anything the Search page sent us takes precedence
      if (home.state) {
        data = { ...data, ...home.state };
      }

      // Hydrate simple UI state
      setSearch(data.search || '');
      findCategory(data.category || 0);
      setCity(data.city || '');

      // ---------------------------------------------------------------
      // 1) ALWAYS load the states list (so dropdown works standalone too)
      // ---------------------------------------------------------------
      let stateList = [];
      try {
        const statesRes = await Api.get('/home/states');
        // Normalize {code,name} → {value,label} once
        stateList = (statesRes.data || []).map((s) => ({
          value: s.value ?? s.code,
          label: s.label ?? s.name,
        }));
        setStates(stateList);
      } catch (err) {
        toast.error('Failed to load states', toastOpts);
      }

      // ---------------------------------------------------------------
      // 2) If we came from Search with a state_code, preselect + load cities
      // ---------------------------------------------------------------
      if (data.state_code) {
        const stateObj =
          stateList.find(
            (s) =>
              String(s.value).toUpperCase() ===
              String(data.state_code).toUpperCase()
          ) || null;

        if (stateObj) {
          setSearchState(stateObj);

          setLoadingCities(true);
          try {
            const { data: cityRows } = await Api.get('/home/cities', {
              params: { code: stateObj.value },
            });
            setCities(
              (cityRows || []).map((c) => ({ value: c.name, label: c.name }))
            );
          } catch (err) {
            toast.error('Failed to load cities', toastOpts);
          } finally {
            setLoadingCities(false);
          }
        }
      }

      // ---------------------------------------------------------------
      // 3) Initial shops fetch — send state only when we also have a city
      // ---------------------------------------------------------------
      const payload = {
        category: data.category || 0,
        search: data.search || '',
        city: data.city || '',
        state_code: data.city ? (data.state_code || '') : '',
        customer: data.customer || 0,
      };

      try {
        const res = await Api.post(
          'shops/shop',
          jwt_encode(payload, process.env.REACT_APP_SECRET_KEY)
        );
        const result = jwt_decode(res.data);
        const status = result.data.status;
        setpageLoading(false);

        if (status === 'failure') {
          setShops(null);
          toast.error(result.data.errors.msg, toastOpts);
        } else if (status === 'success') {
          setCategories(result.data.data.categories);
          setShops(result.data.data.shops);
          setcount(result.data.data.count);
        }
      } catch (err) {
        setpageLoading(false);
        toast.error('Failed to load shops', toastOpts);
      }
    };

    fetchdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------------
  // Webview guard: block pull-to-refresh gesture on inner scroller
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const el = document.getElementById('scrollableDiv');
    if (!el) return;

    let startY = 0;

    const onTouchStart = (e) => {
      startY = e.touches[0].clientY;
    };

    const onTouchMove = (e) => {
      const dy = e.touches[0].clientY - startY;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;

      // Block the gesture if we'd pull down past top or up past bottom
      if ((atTop && dy > 0) || (atBottom && dy < 0)) {
        if (e.cancelable) e.preventDefault();
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Category click (sidebar)
  // ---------------------------------------------------------------------------
  const handleCategory = (event) => {
    event.preventDefault();
    const category = event.target.dataset.category;
    findCategory(category);
    getdata(category, search, searchCity, 0, searchState?.value || '');
  };

  // ---------------------------------------------------------------------------
  // Category dropdown
  // ---------------------------------------------------------------------------
  const handleChange = (event) => {
    event.preventDefault();
    const category = event.target.value;
    findCategory(category);
    getdata(category, search, searchCity, 0, searchState?.value || '');
  };

  // ---------------------------------------------------------------------------
  // State change → load cities only. Do NOT refetch shops yet.
  // ---------------------------------------------------------------------------
  const handleState = async (selected) => {
    setSearchState(selected);
    setCity('');
    setCities([]);

    if (!selected) {
      // State cleared → clear city filter too, refresh list
      getdata(searchCat, search, '');
      return;
    }

    setLoadingCities(true);
    try {
      const { data } = await Api.get('/home/cities', {
        params: { code: selected.value },
      });
      setCities((data || []).map((c) => ({ value: c.name, label: c.name })));
    } catch (err) {
      toast.error('Failed to load cities', toastOpts);
    } finally {
      setLoadingCities(false);
    }

    // Do NOT call getdata() here — wait for the user to pick a city.
  };

  // ---------------------------------------------------------------------------
  // City change → send city + state together
  // ---------------------------------------------------------------------------
  const handleCity = (selected) => {
    const city = selected?.value ?? '';
    setCity(city);
    getdata(searchCat, search, city, 0, searchState?.value || '');
  };

  // ---------------------------------------------------------------------------
  // Search input
  // ---------------------------------------------------------------------------
  const changeSearch = (event) => {
    setSearch(event.target.value);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    getdata(searchCat, search, searchCity, 0, searchState?.value || '');
  };

  const resetSearch = (event) => {
    event.preventDefault();
    setSearch('');
    if (search !== '') getdata(searchCat, '', searchCity, 0, searchState?.value || '');
  };

  // ---------------------------------------------------------------------------
  // Get shops
  // ---------------------------------------------------------------------------
  const getdata = async (category, searchTerm, city, offset = 0, stateCode) => {
    setpageLoading(true);

    const effectiveState = stateCode !== undefined
      ? stateCode
      : (searchState?.value || '');

    const data = {
      category,
      search: searchTerm,
      city,
      // Send state only when a city is present — avoids large state-only queries
      state_code: city ? effectiveState : '',
      customer,
    };

    try {
      const res = await Api.post(
        'shops/category_shop/' + offset,
        jwt_encode(data, process.env.REACT_APP_SECRET_KEY)
      );
      const result = jwt_decode(res.data);
      const status = result.data.status;
      setpageLoading(false);

      if (status === 'failure') {
        setShops(0);
        setcount(0);
        setCatName(result.data.data.category);
        toast.error(result.data.errors.msg, toastOpts);
      } else if (status === 'success') {
        setShops(result.data.data.shops);
        setcount(result.data.data.count);
        setCatName(result.data.data.category);
        if (result.data.data.categories !== null)
          setCategories(result.data.data.categories);
        setCounter(0);
        const el = document.getElementById('scrollableDiv');
        if (el) el.scrollTo(0, 0);
        window.scrollTo(0, 0);
      }
    } catch (err) {
      setpageLoading(false);
      toast.error('Failed to load shops', toastOpts);
    }
  };

  // ---------------------------------------------------------------------------
  // Infinite scroll
  // ---------------------------------------------------------------------------
  const fetchmore = async () => {
    const offset = counter + 1;
    const data = {
      category: searchCat,
      search,
      city: searchCity,
      state_code: searchCity ? (searchState?.value || '') : '',
      customer,
    };

    setTimeout(async () => {
      try {
        const res = await Api.post(
          'shops/category_shop/' + offset,
          jwt_encode(data, process.env.REACT_APP_SECRET_KEY)
        );
        const result = jwt_decode(res.data);
        const status = result.data.status;

        if (status === 'failure') {
          toast.error(result.data.errors.msg, toastOpts);
        } else if (status === 'success') {
          const merged = shops.concat(result.data.data.shops);
          setShops(merged);
          setCounter(offset);
        }
      } catch (err) {
        toast.error('Failed to load more shops', toastOpts);
      }
    }, 1500);
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------
  const moveSingle = (shop_id) => {
    navigate('/single', { state: { id: shop_id } });
  };

  const addFavorite = async (event, shop_id) => {
    if (localStorage.getItem('login-token') !== null) {
      const cust = JSON.parse(localStorage.getItem('login-token'));
      const data = { customer_id: cust.id, franchisee_id: shop_id };

      try {
        const res = await Api.post(
          'customers/favorites',
          jwt_encode(data, process.env.REACT_APP_SECRET_KEY)
        );
        const result = jwt_decode(res.data);
        const status = result.data.status;

        if (status === 'failure') {
          toast.error(result.data.error, toastOpts);
        } else if (status === 'success') {
          if (result.data.action === 'store')
            event.target.className = 'fa fa-heart';
          if (result.data.action === 'remove')
            event.target.className = 'fa fa-heart-o';
          toast.success(result.data.data.msg, toastOpts);
        }
      } catch (err) {
        toast.error('Failed to update favorite', toastOpts);
      }
    } else {
      toast.error('Please login to add favorite !!!', toastOpts);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <Header />
      <section className="w3l-products-page w3l-blog-single w3l-products-4">
        <div className="single blog">
          <div className="wrapper">
            <h3 className="title-main">{cat_name} Shops</h3>
            <div className="d-grid grid-colunm-2 grid-colunm">

              {/* ---------------- Sidebar ---------------- */}
              <div className="right-side-bar">
                <aside className="posts p-4 border">
                  <h3 className="aside-title">All Categories</h3>
                  <ul className="category">
                    <li>
                      <a href="#!" data-category={'All'} onClick={handleCategory}>
                        <span className="fa fa-asterisk"></span>All
                      </a>
                    </li>
                    {categories &&
                      categories.map((category, catIndex) => (
                        <li key={catIndex}>
                          <a
                            href="#."
                            data-category={category.id}
                            onClick={handleCategory}
                          >
                            <span className={category.icon}></span>
                            {category.name} <label>({category.count})</label>
                          </a>
                        </li>
                      ))}
                  </ul>
                </aside>

                <aside className="posts p-4 border">
                  <h3 className="aside-title">Premium Shops</h3>
                  <div className="posts-grid">
                    <a href="#.">
                      <img src="assets/images/b1.jpg" alt="" className="img-responsive img-thumbnail" />
                    </a>
                    <a href="#.">
                      <img src="assets/images/b2.jpg" alt="" className="img-responsive img-thumbnail" />
                    </a>
                    <a href="#.">
                      <img src="assets/images/b3.jpg" alt="" className="img-responsive img-thumbnail" />
                    </a>
                  </div>
                </aside>

                <aside className="">
                  <h3 className="aside-title mb-3">Advertisement</h3>
                  <img src="assets/images/screen.jpg" alt="" className="img-fluid img-responsive" />
                </aside>
              </div>

              {/* ---------------- Main content ---------------- */}
              <div className="tab-content text-left">
                <aside className="top-border shop-toolbar">
                  {/* Pincode search */}
                  <form className="form-inline search-form" onSubmit={handleSearch}>
                    <div className="input-group">
                      <div className="input-group-prepend">
                        <span className="input-group-text" style={{ background: '#f85c70' }}>
                          <button
                            type="reset"
                            className="fa fa-repeat"
                            style={{ color: '#ffffff', border: 'none', outline: 'none', background: 'transparent' }}
                            onClick={resetSearch}
                          ></button>
                        </span>
                      </div>

                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search shop by Pincode"
                        onChange={changeSearch}
                        value={search}
                      />

                      <div className="input-group-append">
                        <div className="input-group-text" style={{ background: '#f85c70' }}>
                          <button
                            type="submit"
                            className="fa fa-search"
                            style={{ color: '#ffffff', border: 'none', outline: 'none', background: 'transparent' }}
                          ></button>
                        </div>
                      </div>
                    </div>
                  </form>

                  {/* State select */}
                  <div className="input-group-btn">
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={searchState}
                      onChange={handleState}
                      options={states.map((s) => ({ value: s.value, label: s.label }))}
                      placeholder="Search By State"
                      isClearable
                      isSearchable
                      styles={selectStyles}
                    />
                  </div>

                  {/* City select */}
                  <div className="input-group-btn">
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={cities.find((c) => c.value === searchCity) || null}
                      onChange={handleCity}
                      options={cities}
                      isLoading={loadingCities}
                      isDisabled={!searchState}
                      placeholder={searchState ? 'Search By City' : 'Select state first'}
                      isClearable
                      isSearchable
                      styles={selectStyles}
                    />
                  </div>

                  {/* Category select */}
                  <div className="input-group-btn">
                    <select
                      className="btn btn-default"
                      name="ext"
                      value={searchCat}
                      onChange={handleChange}
                      style={{ textAlign: 'justify' }}
                    >
                      <option value="0">Sort By Categories</option>
                      {categories &&
                        categories.map((category, catIndex) => (
                          <option key={catIndex} value={category.id}>
                            {category.name} ({category.count})
                          </option>
                        ))}
                    </select>
                  </div>
                </aside>

                {/* Scrollable list — with webview-safe scroll behavior */}
                <div
                  id="scrollableDiv"
                  style={{
                    height: 1114,
                    overflow: 'auto',
                    display: 'flex',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    touchAction: 'pan-y',
                  }}
                >
                  {pageloading ? (
                    <Loading />
                  ) : shops !== null && shops !== 0 && shops.length > 0 ? (
                    <InfiniteScroll
                      dataLength={shops.length}
                      next={fetchmore}
                      hasMore={shops.length != count}
                      endMessage={
                        <p
                          className="alert alert-success mt-4"
                          style={{ textAlign: 'center' }}
                        >
                          <b>Yay! You have seen it all</b>
                        </p>
                      }
                      loader={<Loading />}
                      scrollableTarget="scrollableDiv"
                    >
                      <div className="d-grid grid-col-2">
                        {shops.map((shop, shopIndex) => (
                          <div className="product" key={shopIndex}>
                            <a
                              href="."
                              onClick={(e) => {
                                e.preventDefault();
                                moveSingle(shop.id);
                              }}
                            >
                              <img
                                src={shop.image ? shop.image : default_image}
                                className="img-responsive"
                                alt={shop.name}
                              />
                            </a>
                            <div className="info-bg">
                              <h5>
                                <a
                                  href="."
                                  onClick={(e) => {
                                    e.preventDefault();
                                    moveSingle(shop.id);
                                  }}
                                >
                                  {shop.name}
                                </a>
                              </h5>
                              <p className="mb-0">
                                {shop.city} - {shop.pincode}
                              </p>
                              <p className="mt-0" style={{ fontSize: '12px' }}>
                                {shop.address.slice(0, 45)}...
                              </p>
                              <ul className="d-flex">
                                <li>
                                  <a
                                    href="."
                                    onClick={(e) => {
                                      e.preventDefault();
                                      moveSingle(shop.id);
                                    }}
                                  >
                                    <span className="fa fa-eye"></span> Explore Now
                                  </a>
                                </li>
                                <li className="margin-effe">
                                  <a href="#." title="Favorite Shops List">
                                    <span
                                      onClick={(event) => {
                                        event.preventDefault();
                                        addFavorite(event, shop.id);
                                      }}
                                      className={shop.store ? 'fa fa-heart' : 'fa fa-heart-o'}
                                    ></span>
                                  </a>
                                </li>
                                <li>
                                  <a href="#sahre" title="Share">
                                    <span className="fa fa-share"></span>
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                    </InfiniteScroll>
                  ) : (
                    <div style={{ width: '100%' }}>
                      <center>
                        <span>
                          <strong>Shops Not Found !!!</strong>
                        </span>
                      </center>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}

export default Shops;